/**
 * Web building blocks for the Connected apps settings panel: list rows, the app
 * mark, toggles and the empty state, kept in one place so the list and the
 * detail view do not drift into two design languages.
 *
 * Everything here is HTML + CSS custom properties (see the tabbio-web-design
 * skill): no react-native primitives, no `theme.*` reads.
 */
import type { CSSProperties, ReactNode } from 'react';
import { MaterialSymbol } from '../../icons/MaterialSymbol';

export function formatWebDate(
  value: string | null | undefined,
  locale: string,
) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(locale);
}

/** First letter that carries meaning, so a logo-less app still gets a mark. */
function initialFor(value: string) {
  const match = value.match(/\p{L}|\p{N}/u);
  return match ? match[0].toUpperCase() : 'T';
}

export function PanelBackButton({
  label,
  onPress,
}: {
  label: string;
  onPress: () => void;
}) {
  return (
    <button type="button" onClick={onPress} style={BACK_BUTTON}>
      <MaterialSymbol
        name="arrow_back"
        size={16}
        className="tabbio-rtl-flip"
        style={{ color: 'var(--text-secondary)' }}
      />
      {label}
    </button>
  );
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return <div style={SECTION_TITLE}>{children}</div>;
}

export function HelpText({ children }: { children: ReactNode }) {
  return <p style={HELP_TEXT}>{children}</p>;
}

export function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div style={EMPTY}>
      <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-primary)' }}>
        {title}
      </div>
      <p style={{ ...HELP_TEXT, margin: 0 }}>{body}</p>
    </div>
  );
}

export function ListRow({
  title,
  subtitle,
  leading,
  onPress,
}: {
  title: string;
  subtitle?: string;
  leading?: ReactNode;
  onPress: () => void;
}) {
  return (
    <button type="button" onClick={onPress} style={LIST_ROW}>
      {leading ? <span style={LIST_ROW_LEADING}>{leading}</span> : null}
      <span style={{ flex: 1, minWidth: 0, textAlign: 'start' }}>
        <span style={LIST_ROW_TITLE} dir="auto">
          {title}
        </span>
        {subtitle ? (
          <span style={LIST_ROW_SUB} dir="auto">
            {subtitle}
          </span>
        ) : null}
      </span>
      <MaterialSymbol
        name="chevron_right"
        size={18}
        className="tabbio-rtl-flip"
        style={{ color: 'var(--text-tertiary)' }}
      />
    </button>
  );
}

export function AppMark({
  name,
  logoUrl,
  size = 32,
}: {
  name: string;
  logoUrl?: string | null;
  size?: number;
}) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt=""
        style={{
          width: size,
          height: size,
          borderRadius: 8,
          objectFit: 'contain',
          display: 'block',
        }}
      />
    );
  }

  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: 'var(--hover-strong)',
        color: 'var(--text-secondary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: Math.round(size * 0.44),
        fontWeight: 500,
        flexShrink: 0,
      }}
    >
      {initialFor(name)}
    </span>
  );
}

export function ToggleRow({
  title,
  body,
  checked,
  onChange,
  disabled,
}: {
  title: string;
  body?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div style={{ ...ROW, opacity: disabled ? 0.45 : 1 }}>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={ROW_TITLE}>{title}</span>
        {body ? <span style={ROW_BODY}>{body}</span> : null}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={title}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        style={{
          width: 44,
          height: 26,
          borderRadius: 9999,
          border: '1px solid var(--input-border)',
          background: checked ? 'var(--text-primary)' : 'transparent',
          padding: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: checked ? 'flex-end' : 'flex-start',
          cursor: disabled ? 'default' : 'pointer',
          transition: 'background 150ms',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            background: checked ? 'var(--bg)' : 'var(--text-tertiary)',
            display: 'block',
          }}
        />
      </button>
    </div>
  );
}

export function DestructiveButton({
  label,
  onPress,
  disabled,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onPress}
      disabled={disabled}
      style={{
        height: 40,
        paddingInline: 18,
        borderRadius: 9999,
        border: '1px solid var(--input-border)',
        background: 'transparent',
        color: '#d93025',
        fontSize: 14,
        fontWeight: 500,
        fontFamily: 'inherit',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        alignSelf: 'flex-start',
      }}
    >
      {label}
    </button>
  );
}

// --- styles ----------------------------------------------------------------

export const PANEL: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const SECTION_TITLE: CSSProperties = {
  fontSize: 15,
  fontWeight: 500,
  color: 'var(--text-primary)',
  marginBlockStart: 12,
};

const HELP_TEXT: CSSProperties = {
  fontSize: 13,
  lineHeight: '19px',
  color: 'var(--text-secondary)',
};

const EMPTY: CSSProperties = {
  padding: '18px 20px',
  borderRadius: 20,
  background: 'var(--hover)',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const LIST_ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  minHeight: 60,
  padding: '12px 14px',
  borderRadius: 14,
  border: 0,
  background: 'transparent',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const LIST_ROW_LEADING: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
};

const LIST_ROW_TITLE: CSSProperties = {
  display: 'block',
  fontSize: 14,
  fontWeight: 500,
  color: 'var(--text-primary)',
};

const LIST_ROW_SUB: CSSProperties = {
  display: 'block',
  fontSize: 12,
  color: 'var(--text-secondary)',
  marginBlockStart: 2,
};

const ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  paddingBlock: 12,
  borderBlockStart: '1px solid var(--input-border)',
};

const ROW_TITLE: CSSProperties = {
  display: 'block',
  fontSize: 14,
  fontWeight: 500,
  color: 'var(--text-primary)',
  textAlign: 'start',
};

const ROW_BODY: CSSProperties = {
  display: 'block',
  fontSize: 12.5,
  lineHeight: '18px',
  color: 'var(--text-secondary)',
  marginBlockStart: 2,
  textAlign: 'start',
};

const BACK_BUTTON: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  border: 0,
  background: 'transparent',
  color: 'var(--text-secondary)',
  fontSize: 13,
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: 'pointer',
  padding: 0,
  alignSelf: 'flex-start',
  marginBlockEnd: 4,
};
